/**
 * The Azure Storage arm of the MCP `ui_open_tab` handler (spec §6.2 / §11):
 * `type: 'storage-explorer'` routes to the ONE opener (`openStorageExplorerTab`,
 * `source: 'mcp'`), and every SQL-shaped tab type is gated on the connection's
 * capability flags — the same `engineSupports` reads that fill
 * `ConnectionSnapshotItem.supportsQueryEditor / supportsTableExplorer` for the
 * agent — answering `STORAGE_ENGINE_UNSUPPORTED.message` so main audits the
 * refusal as an `unsupported_engine` error (never a wire code).
 */
import { engineSupports, isValidEngineId, type DatabaseEngineId } from '../../../core/engines';
import { STORAGE_ENGINE_UNSUPPORTED } from '../../../core/engines/azureStorageRefusal';
import type { DirectoryRef, StorageResourceRef } from '../../../types/azureStorage';
import { parentDirectoryRef } from '../../../core/engines/azureStorageFilePaths';
import { isWithinStorageRoot } from '../../workspace/storage-explorer/utils/storagePaths';
import { storageResourceRefSchema } from '../../../types/azureStorageSchema';
import type { Connection } from '../../../types/electron';
import type { UiOpenTabInput, UiOpenTabResult } from '../../../types/mcpUiControl';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { openStorageExplorerTab } from '../../workspace/storage-explorer/utils/openStorageExplorerTab';
import { hnsLocationForTab, isOutsideAttachmentScope } from '../../workspace/storage-explorer/hns/hnsOpenTab';

const findConnection = (connectionId: string): Connection | undefined =>
  useConnectionStore.getState().connections.find((c) => c.id === connectionId);

const engineIdOf = (connection: Connection): DatabaseEngineId | undefined =>
  isValidEngineId(connection.engine ?? '') ? (connection.engine as DatabaseEngineId) : undefined;

/**
 * Refuses a query / table-explorer / notebook tab on a connection whose engine
 * has no such surface. `undefined` = proceed (unknown connection ids fall
 * through to the type-specific handling, which reports them as before).
 */
export function refuseTabTypeForConnection(input: UiOpenTabInput): UiOpenTabResult | undefined {
  if (!input.connectionId || input.type === 'storage-explorer') return undefined;
  const connection = findConnection(input.connectionId);
  const engineId = connection ? engineIdOf(connection) : undefined;
  if (!engineId) return undefined;
  const capability = input.type === 'table-explorer' ? 'supportsTableExplorer' : 'supportsQueryEditor';
  return engineSupports(engineId, 'workspaceFeatures', capability) ? undefined : { success: false, error: STORAGE_ENGINE_UNSUPPORTED.message };
}

/** Folder containing a blob: `a/b/c.png` → prefix `a/b/`; a top-level blob → the container root. */
function folderOf(ref: Extract<StorageResourceRef, { kind: 'blob' }>): StorageResourceRef {
  const slash = ref.name.lastIndexOf('/');
  return slash < 0 ? { kind: 'container', container: ref.container } : { kind: 'blobPrefix', container: ref.container, prefix: ref.name.slice(0, slash + 1) };
}

type FileLocation = Extract<StorageResourceRef, { kind: 'share' | 'directory' | 'file' }>;
const isFileLocation = (ref: StorageResourceRef): ref is FileLocation => ref.kind === 'share' || ref.kind === 'directory' || ref.kind === 'file';
const withSnapshot = <T extends FileLocation>(ref: T, snapshot: string | undefined): T => (snapshot && !ref.snapshot ? { ...ref, snapshot } : ref);

/** Tab root: the attachment's own resource on a SAS-scoped connection, else the container / the share (snapshot kept). */
export function storageRootFor(connection: Connection, resource: StorageResourceRef): StorageResourceRef {
  const scope = connection.azureStorageScope;
  const snapshot = isFileLocation(resource) ? resource.snapshot : undefined;
  if (scope?.kind === 'resource') return isFileLocation(scope.resource) ? withSnapshot(scope.resource, snapshot) : scope.resource;
  if (resource.kind === 'container' || resource.kind === 'blobPrefix' || resource.kind === 'blob') return { kind: 'container', container: resource.container };
  if (isFileLocation(resource)) return { kind: 'share', share: resource.share, ...(snapshot ? { snapshot } : {}) };
  if (resource.kind === 'hnsPath') return { kind: 'hnsPath', fileSystem: resource.fileSystem, path: '', isDirectory: true }; // S8
  return resource;
}

/** §3.7 rule 1, renderer side: does the connection's attachment contain this folder (a live root admits its snapshots)? */
function attachmentContains(connection: Connection, folder: DirectoryRef): boolean {
  const scope = connection.azureStorageScope;
  if (scope?.kind !== 'resource' || scope.resource.kind === 'account' || (scope.resource.kind === 'service' && scope.resource.service === 'file')) return true;
  const root = scope.resource;
  return isFileLocation(root) && root.kind !== 'file' && isWithinStorageRoot(withSnapshot(root, folder.snapshot), folder);
}

/** S7-09: a file opens its parent folder when the attachment contains it, else the single-file tab rooted at the file. */
function fileLocationFor(connection: Connection, file: Extract<StorageResourceRef, { kind: 'file' }>): { root?: StorageResourceRef; location: StorageResourceRef } {
  const parent = parentDirectoryRef(file);
  if (!parent || !attachmentContains(connection, parent)) return { root: file, location: file };
  return { location: parent.path === '' ? { kind: 'share', share: parent.share, ...(parent.snapshot ? { snapshot: parent.snapshot } : {}) } : parent };
}

export function handleStorageExplorerOpenTab(input: UiOpenTabInput): UiOpenTabResult {
  if (!input.connectionId) return { success: false, error: 'connectionId is required for storage-explorer tabs' };
  const connection = findConnection(input.connectionId);
  if (!connection) return { success: false, error: `Connection not found: ${input.connectionId}` };
  if (connection.engine !== 'azurestorage') {
    return { success: false, error: `Connection ${input.connectionId} is a ${connection.engine ?? 'SQL'} connection; storage-explorer tabs open on Azure Storage connections only.` };
  }
  const parsed = storageResourceRefSchema.safeParse(input.resource);
  if (!parsed.success) {
    return { success: false, error: 'resource is required for storage-explorer tabs: { kind: "container", container } or { kind: "blobPrefix", container, prefix }' };
  }
  const file = parsed.data.kind === 'file' ? fileLocationFor(connection, parsed.data) : undefined;
  const location = file ? file.location : parsed.data.kind === 'blob' ? folderOf(parsed.data) : parsed.data.kind === 'hnsPath' ? hnsLocationForTab(parsed.data) : parsed.data;
  if (location.kind !== 'container' && location.kind !== 'blobPrefix' && location.kind !== 'table' && location.kind !== 'hnsPath' && !isFileLocation(location)) {
    return { success: false, error: `${location.kind} resources arrive with a later release; open a container, blob prefix, Data Lake path, table, share or share folder.` };
  }
  const rootParsed = input.root ? storageResourceRefSchema.safeParse(input.root) : undefined;
  const root = rootParsed?.success ? rootParsed.data : file?.root ?? storageRootFor(connection, location);
  // S8: a resource-scoped attachment never opens a location outside its scope (the Blob ⇄ Data Lake equivalence counts as inside).
  if (connection.azureStorageScope?.kind === 'resource' && isOutsideAttachmentScope(connection.azureStorageScope.resource, location)) {
    return { success: false, error: 'Tab not found: the requested location lies outside this connection\'s attachment scope.' };
  }
  const tabId = openStorageExplorerTab({ connectionId: input.connectionId, root, resource: location, source: 'mcp' });
  return tabId ? { success: true, tabId } : { success: false, error: `Connection not found: ${input.connectionId}` };
}
